/**
 * TomerBot Chat Widget for Shopify
 * Hebrew Product Recommendation Chatbot
 *
 * Features:
 * - Hebrew/RTL support
 * - Session management with sessionStorage
 * - Customer detection for logged-in users
 * - Backend-managed conversation history
 * - Mobile responsive
 * - Error handling with retry logic
 */

// Session Management Class
class TomerBotSession {
  constructor() {
    this.sessionId = this.getOrCreateSessionId();
  }

  getOrCreateSessionId() {
    let sessionId = sessionStorage.getItem('tomerbot_session_id');

    if (!sessionId) {
      // Generate UUID for modern browsers
      if (typeof crypto !== 'undefined' && crypto.randomUUID) {
        sessionId = crypto.randomUUID();
      } else {
        // Fallback UUID generation for older browsers
        sessionId = 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
          var r = Math.random() * 16 | 0;
          var v = c == 'x' ? r : (r & 0x3 | 0x8);
          return v.toString(16);
        });
      }

      sessionStorage.setItem('tomerbot_session_id', sessionId);
    }

    return sessionId;
  }

  getSessionId() {
    return this.sessionId;
  }

  clearSession() {
    sessionStorage.removeItem('tomerbot_session_id');
    this.sessionId = this.getOrCreateSessionId();
  }
}

// Customer Detection Class
class ShopifyCustomerDetector {
  static getCustomerData() {
    // Method 1: Check for Liquid-injected customer data
    if (window.tomerBotCustomer) {
      return {
        id: window.tomerBotCustomer.id.toString(),
        email: window.tomerBotCustomer.email
      };
    }

    // Method 2: Check Adoric integration data
    if (window.Adoric_State?.customer) {
      return {
        id: window.Adoric_State.customer.id.toString(),
        email: window.Adoric_State.customer.email
      };
    }

    // Method 3: Check for global customer data
    if (window.customerData) {
      return {
        id: window.customerData.id.toString(),
        email: window.customerData.email
      };
    }

    // Method 4: Check Shopify Analytics
    if (window.ShopifyAnalytics?.meta?.page?.customerId) {
      return {
        id: window.ShopifyAnalytics.meta.page.customerId.toString(),
        email: window.customer?.email
      };
    }

    // No customer logged in
    return null;
  }
}

// Main Chat Client Class
class TomerBotClient {
  constructor(apiBaseUrl = '') {
    this.apiUrl = apiBaseUrl || 'https://homecenter.magicetl.com/public/shopify/controller/tomerbot-chat';
    this.session = new TomerBotSession();
  }

  async sendMessage(userMessage, options = {}) {
    try {
      // Build request payload
      const payload = {
        user_query: userMessage,
        session_id: this.session.getSessionId()
      };

      // Add customer data if user is logged in
      const customerData = ShopifyCustomerDetector.getCustomerData();
      if (customerData) {
        payload.customer = customerData;
      }

      console.log('TomerBot - Sending request:', payload);

      // Send request
      const response = await fetch(this.apiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload)
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      // Branch on response format: SSE stream vs regular JSON
      const contentType = response.headers.get('content-type') || '';
      if (contentType.includes('text/event-stream')) {
        return await this.handleSSEResponse(response, options.onChunk);
      }

      // Regular JSON mode (default)
      const data = await response.json();

      console.log('TomerBot - Received response:', data);

      return {
        success: data.success !== false,
        message: data.response || 'מצטערים, לא התקבלה תשובה.',
        sessionInfo: data.session_info || null
      };

    } catch (error) {
      console.error('TomerBot API Error:', error);
      return {
        success: false,
        message: 'מצטערים, אירעה שגיאה. אנא נסו שוב.',
        sessionInfo: null
      };
    }
  }

  /**
   * Handle SSE streaming response.
   * Reads the stream, parses SSE events, calls onChunk for each text fragment.
   *
   * @param {Response} response - The fetch Response object
   * @param {Function|null} onChunk - Callback invoked with each text chunk: fn(text)
   * @returns {Object} { success, message, sessionInfo }
   */
  async handleSSEResponse(response, onChunk) {
    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';
    let fullMessage = '';
    let sessionInfo = null;

    try {
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });

        // Process complete lines; keep the trailing partial line buffered
        const lines = buffer.split('\n');
        buffer = lines.pop();

        for (const line of lines) {
          const trimmedLine = line.trim();
          if (!trimmedLine || !trimmedLine.startsWith('data: ')) continue;

          let eventData;
          try {
            eventData = JSON.parse(trimmedLine.substring(6));
          } catch (e) {
            console.warn('TomerBot SSE: Failed to parse JSON:', trimmedLine);
            continue;
          }

          if (eventData.type === 'chunk') {
            fullMessage += eventData.data;
            if (onChunk) {
              onChunk(eventData.data);
              // Yield to the browser paint cycle for real-time updates
              await new Promise(resolve => requestAnimationFrame(resolve));
            }
          } else if (eventData.type === 'done') {
            sessionInfo = eventData.session_info || null;
          } else if (eventData.type === 'error') {
            throw new Error(eventData.message || 'מצטערים, אירעה שגיאה. אנא נסו שוב.');
          }
        }
      }

      // Process any remaining data left in the buffer
      const remainingLine = buffer.trim();
      if (remainingLine && remainingLine.startsWith('data: ')) {
        try {
          const lastEvent = JSON.parse(remainingLine.substring(6));
          if (lastEvent.type === 'chunk') {
            fullMessage += lastEvent.data;
            if (onChunk) onChunk(lastEvent.data);
          } else if (lastEvent.type === 'done') {
            sessionInfo = lastEvent.session_info || null;
          }
        } catch (e) {
          console.warn('TomerBot SSE: Failed to parse remaining buffer:', remainingLine);
        }
      }

      console.log('TomerBot - SSE stream complete, message length:', fullMessage.length);

      return {
        success: true,
        message: fullMessage || 'מצטערים, לא התקבלה תשובה.',
        sessionInfo: sessionInfo
      };

    } catch (error) {
      console.error('TomerBot SSE Error:', error);
      // Return whatever streamed before the error, if anything
      if (fullMessage) {
        return { success: true, message: fullMessage, sessionInfo: sessionInfo };
      }
      throw error;
    }
  }

  // Send message with retry logic
  async sendMessageWithRetry(userMessage, maxRetries = 3, options = {}) {
    for (let attempt = 1; attempt <= maxRetries; attempt++) {
      try {
        const response = await this.sendMessage(userMessage, options);

        if (response.success) {
          return response;
        }

        // If not successful but no network error, don't retry
        if (attempt === maxRetries) {
          return response;
        }

      } catch (error) {
        console.warn(`TomerBot attempt ${attempt} failed:`, error);

        if (attempt === maxRetries) {
          return {
            success: false,
            message: 'מצטערים, אירעה שגיאה. אנא נסו שוב.',
            sessionInfo: null
          };
        }

        // Wait before retry (exponential backoff)
        await new Promise(resolve => setTimeout(resolve, Math.pow(2, attempt) * 1000));
      }
    }
  }

  // Clear session (starts new conversation)
  clearHistory() {
    this.session.clearSession();
  }

  // Get current session ID
  getSessionId() {
    return this.session.getSessionId();
  }
}

// Main Chat Widget Class
class TomerBotWidget {
  constructor(containerId, options = {}) {
    this.container = document.getElementById(containerId);
    if (!this.container) {
      console.error('TomerBot: Container element not found:', containerId);
      return;
    }
    
    this.client = new TomerBotClient(options.apiBaseUrl || '');
    this.messages = [];
    this.messageFeedback = {}; // Store feedback for each message
    this.isOpen = false;
    this.isRTL = options.rtl !== false; // Default to RTL

    // Load previous conversation and feedback
    this.loadConversationHistory();
    this.loadFeedbackHistory();

    this.init();
  }
  
  init() {
    this.createWidget();
    this.attachEventListeners();
    this.restoreMessagesInUI();
    this.checkPrivacyPolicyAcceptance();

    // Restore open state after page reload
    if (sessionStorage.getItem('tomerbot_is_open') === 'true') {
      this.toggleChat();
    }
  }
  
  createWidget() {
    const rtlClass = this.isRTL ? 'tomerbot-rtl' : 'tomerbot-ltr';
    const direction = this.isRTL ? 'rtl' : 'ltr';
    const textAlign = this.isRTL ? 'right' : 'left';
    const buttonPosition = this.isRTL ? 'left: 20px;' : 'right: 20px;';
    const chatPosition = this.isRTL ? 'left: 0;' : 'right: 0;';
    const heading = 'אני תומר, היועץ הדיגיטלי של הום סנטר.';
    const body = `אשמח להציע לך פתרונות ומוצרים לתחזוקת הבית.
יתכן שתשובותיי לא תמיד תהיינה מדויקות, וידרשו פנייה לאיש מקצוע.`;
    const agreement = `בטרם נתחיל, עליי לקבל את הסכמתך כי שיחתנו תתקיים בהתאם <a href="/pages/ai-tomer-terms-of-use" target="_blank">לתנאי השימוש</a>&nbsp;<a href="/pages/tomer-ai-privacy-policy" target="_blank">ומדיניות הפרטיות</a>`;

    this.container.innerHTML = `
      <div id="tomerbot-widget" class="tomerbot-widget ${rtlClass}">
        <!-- Chat Toggle Button -->
        <button id="tomerbot-toggle" class="tomerbot-toggle" 
                title="${this.isRTL ? 'פתח צ\'אט עם תומר הבוט' : 'Open Tomer Bot Chat'}"
                aria-label="${this.isRTL ? 'פתח צ\'אט עם תומר הבוט' : 'Open Tomer Bot Chat'}">
          
          <div class="tomerbot-sparkles">
            <span class="sparkle sparkle-1">✨</span>
            <span class="sparkle sparkle-2">⭐</span>
            <span class="sparkle sparkle-3">✨</span>
            <span class="sparkle sparkle-4">💫</span>
          </div>
        </button>

        <!-- Dismiss (hide until next page load) -->
        <button id="tomerbot-dismiss" class="tomerbot-dismiss"
                title="${this.isRTL ? 'הסתר' : 'Hide'}"
                aria-label="${this.isRTL ? 'הסתר' : 'Hide'}">✕</button>

        <!-- Chat Window -->
        <div id="tomerbot-chat" class="tomerbot-chat disabled" style="display: none; direction: ${direction};">
          <!-- Header -->
          <div class="tomerbot-header">
            <div class="tomerbot-header-content">
              <span class="tomerbot-title">תומר | מומחה AI של הום סנטר</span>
              <button id="tomerbot-close" class="tomerbot-close-btn" 
                      title="${this.isRTL ? 'סגור צ\'אט' : 'Close Chat'}"
                      aria-label="${this.isRTL ? 'סגור צ\'אט' : 'Close Chat'}">
                ✕
              </button>
            </div>
          </div>
          
          <!-- Messages Container -->
          <span class="bot-avatar"></span>
          <div id="tomerbot-messages" class="tomerbot-messages">
            <div class="bot-message welcome-message">
              <div class="welcome-header">
                <div class="welcome-text">
                  <h4>${heading}</h4>
                </div>
              </div>
              <div class="welcome-content">
                <p><b>${body}</b></p><br>
                <div class="mini-agreement-form">
                  <p class="agreement-text">${agreement}</p>
                  <button id="accecpts_privacy_policy"><span class="agree-icon"></span></button>
                </div>
              </div>
            </div>
          </div>
          
          <!-- Input Area -->
          <div class="tomerbot-input" style="direction: ${direction};">
            <input 
              id="tomerbot-input-field" 
              type="text" 
              placeholder="במה אוכל לעזור לך?"
              style="direction: ${direction}; text-align: ${textAlign};"
              maxlength="500"
              disabled
            />
            <button id="tomerbot-send"
                    title="${this.isRTL ? 'שלח הודעה' : 'Send Message'}"
                    aria-label="${this.isRTL ? 'שלח הודעה' : 'Send Message'}">
              <span class="send-icon"></span>
            </button>
          </div>
          
          <!-- Footer -->
          <div class="tomerbot-footer">
            <button id="tomerbot-clear" class="tomerbot-clear-btn">
              <span class="new-chat-icon"></span>
            </button>
          </div>
        </div>
      </div>
    `;
  }
  
  attachEventListeners() {
    // Toggle chat window
    const toggleBtn = document.getElementById('tomerbot-toggle');
    const closeBtn = document.getElementById('tomerbot-close');
    
    toggleBtn?.addEventListener('click', () => this.toggleChat());
    closeBtn?.addEventListener('click', () => this.toggleChat());

    // Dismiss button - hide the whole widget until the next page load
    const dismissBtn = document.getElementById('tomerbot-dismiss');
    dismissBtn?.addEventListener('click', (e) => {
      e.stopPropagation();
      const widget = document.getElementById('tomerbot-widget');
      if (widget) widget.style.display = 'none';
    });
    
    // Send message events
    const sendBtn = document.getElementById('tomerbot-send');
    const inputField = document.getElementById('tomerbot-input-field');
    
    sendBtn?.addEventListener('click', () => this.sendMessage());
    
    inputField?.addEventListener('keypress', (e) => {
      if (e.key === 'Enter') {
        this.sendMessage();
      }
    });
    
    // Clear conversation
    const clearBtn = document.getElementById('tomerbot-clear');
    clearBtn?.addEventListener('click', () => this.clearConversation());
    
    // Close chat when clicking outside
    document.addEventListener('click', (e) => {
      const widget = document.getElementById('tomerbot-widget');
      if (this.isOpen && widget && !widget.contains(e.target)) {
        this.toggleChat();
      }
    });
    
    // Handle escape key
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.isOpen) {
        this.toggleChat();
      }
    });

    // Privacy policy button handler
    const privacyButton = document.getElementById('accecpts_privacy_policy');
    privacyButton?.addEventListener('click', () => {
      this.handlePrivacyPolicyButton();
    });
  }
  
  toggleChat() {
    const chatWindow = document.getElementById('tomerbot-chat');
    const toggleBtn = document.getElementById('tomerbot-toggle');
    
    this.isOpen = !this.isOpen;
    sessionStorage.setItem('tomerbot_is_open', this.isOpen ? 'true' : 'false');

    if (this.isOpen) {
      chatWindow.style.display = 'flex';
      toggleBtn.classList.add('active');
      
      // Add shimmer effect to show activity
      toggleBtn.classList.add('shimmer-active');
      setTimeout(() => toggleBtn.classList.remove('shimmer-active'), 1000);
      
      // Focus on input field
      setTimeout(() => {
        const inputField = document.getElementById('tomerbot-input-field');
        inputField?.focus();
      }, 100);
      
    } else {
      chatWindow.style.display = 'none';
      toggleBtn.classList.remove('active');
    }
  }
  
  async sendMessage() {
    const inputField = document.getElementById('tomerbot-input-field');
    const userMessage = inputField?.value?.trim();
    
    if (!userMessage) return;
    
    // Clear input
    inputField.value = '';
    
    // Add user message to chat
    this.addMessage('user', userMessage);

    // Show typing indicator
    this.showTypingIndicator();

    // Track SSE streaming state
    let streamingState = null;
    let htmlBuffer = ''; // raw accumulated HTML — re-rendered whole on each chunk

    try {
      // onChunk fires only in SSE mode; regular mode leaves streamingState null
      const onChunk = (text) => {
        if (!streamingState) {
          // First chunk — replace typing indicator with an empty bot bubble
          this.hideTypingIndicator();
          streamingState = this.addEmptyBotMessage();
          htmlBuffer = '';
        }

        // Accumulate raw HTML and re-render the WHOLE buffer each chunk.
        // The browser re-parses the full coherent string, so tags that span
        // chunk boundaries (e.g. "<stro" + "ng>") render correctly. An incomplete
        // trailing tag simply isn't shown until the next chunk completes it.
        htmlBuffer += text;
        streamingState.contentElement.innerHTML = htmlBuffer;

        const messagesContainer = document.getElementById('tomerbot-messages');
        if (messagesContainer) {
          messagesContainer.scrollTop = messagesContainer.scrollHeight;
        }
      };

      // Send to TomerBot API with retry
      const response = await this.client.sendMessageWithRetry(userMessage, 3, { onChunk });

      // Remove typing indicator (in case no chunks arrived — regular mode)
      this.hideTypingIndicator();

      if (streamingState) {
        // SSE mode — finalize the streamed bubble (feedback buttons, history, link cleanup)
        this.finalizeBotMessage(
          streamingState.messageDiv,
          streamingState.contentElement,
          response.message,
          streamingState.messageId
        );
      } else {
        // Regular mode — add the complete message (unchanged behavior)
        this.addMessage('bot', response.message);
      }

    } catch (error) {
      this.hideTypingIndicator();
      if (streamingState) {
        this.finalizeBotMessage(
          streamingState.messageDiv,
          streamingState.contentElement,
          'מצטערים, אירעה שגיאה. אנא נסו שוב.',
          streamingState.messageId
        );
      } else {
        this.addMessage('bot', 'מצטערים, אירעה שגיאה. אנא נסו שוב.');
      }
    }
  }

  /**
   * Create an empty bot bubble for SSE streaming.
   * Returns DOM references used to update content in real time.
   */
  addEmptyBotMessage() {
    const messagesContainer = document.getElementById('tomerbot-messages');
    const messageId = `msg_${Date.now()}_${Math.random().toString(36).substring(2, 11)}`;

    const messageDiv = document.createElement('div');
    messageDiv.className = 'bot-message';

    const contentWrapper = document.createElement('div');
    contentWrapper.className = 'bot-message-content';

    messageDiv.appendChild(contentWrapper);
    messagesContainer.appendChild(messageDiv);
    messagesContainer.scrollTop = messagesContainer.scrollHeight;

    return { messageDiv, contentElement: contentWrapper, messageId };
  }

  /**
   * Finalize a streamed bot bubble: clean up links, attach feedback buttons,
   * and save to history. Re-renders the complete content once.
   */
  finalizeBotMessage(messageDiv, contentElement, fullContent, messageId) {
    // Convert markdown-style links to HTML (no-op when bot sends HTML)
    let htmlContent = fullContent.replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2" target="_blank" rel="noopener">$1</a>');

    // Clean up radio link formatting
    if (htmlContent.includes('class="radio"')) {
      htmlContent = htmlContent.replace(/-\s*(<a\s[^>]*class="radio")/g, '$1');
      htmlContent = htmlContent.replace(/<br\s*\/?>\s*(?=<a\s[^>]*class="radio")/g, '');
    }

    contentElement.innerHTML = htmlContent;

    // Feedback buttons
    const feedbackContainer = document.createElement('div');
    feedbackContainer.className = 'bot-message-feedback';
    feedbackContainer.setAttribute('data-message-id', messageId);
    feedbackContainer.innerHTML = `
      <span class="feedback-text">עזרת לי</span>
        <button class="feedback-btn like-btn" data-feedback="like" title="תשובה מועילה">
          <span class="feedback-icon"></span>
        </button>
        <button class="feedback-btn dislike-btn" data-feedback="dislike" title="תשובה לא מועילה">
          <span class="feedback-icon"></span>
        </button>
    `;

    // Radio-style links
    const radioLinks = contentElement.querySelectorAll('a.radio');
    radioLinks.forEach(radio => {
      radio.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        radioLinks.forEach(r => r.classList.remove('selected'));
        radio.classList.add('selected');
        this.saveRadioSelection(messageId, radio.textContent.trim());
        const selectedText = radio.textContent.trim();
        setTimeout(async () => {
          this.addMessage('user', selectedText);
          this.showTypingIndicator();
          try {
            const response = await this.client.sendMessageWithRetry(selectedText);
            this.hideTypingIndicator();
            this.addMessage('bot', response.message);
          } catch (error) {
            this.hideTypingIndicator();
            this.addMessage('bot', 'מצטערים, אירעה שגיאה. אנא נסו שוב.');
          }
        }, 300);
      });
    });

    messageDiv.appendChild(feedbackContainer);

    const likeBtn = feedbackContainer.querySelector('.like-btn');
    const dislikeBtn = feedbackContainer.querySelector('.dislike-btn');

    likeBtn?.addEventListener('click', (e) => {
      e.stopPropagation();
      this.handleFeedback(messageId, 'like', fullContent, likeBtn, dislikeBtn);
    });

    dislikeBtn?.addEventListener('click', (e) => {
      e.stopPropagation();
      this.handleFeedback(messageId, 'dislike', fullContent, likeBtn, dislikeBtn);
    });

    // Save to messages array and conversation history
    this.messages.push({
      role: 'bot',
      content: fullContent,
      timestamp: new Date().toISOString(),
      id: messageId
    });
    this.saveConversationHistory();

    const messagesContainer = document.getElementById('tomerbot-messages');
    if (messagesContainer) {
      messagesContainer.scrollTop = messagesContainer.scrollHeight;
    }
  }
  
  addMessage(role, content) {
    // Generate unique message ID for bot messages
    const messageId = role === 'bot' ? `msg_${Date.now()}_${Math.random().toString(36).substr(2, 9)}` : null;

    // Add to local messages array for UI display
    this.messages.push({
      role,
      content,
      timestamp: new Date().toISOString(),
      id: messageId // Store message ID for feedback tracking
    });

    // Add to UI with the message ID
    this.addMessageToUI(role, content, messageId);

    // Save conversation history
    this.saveConversationHistory();
  }
  
  addMessageToUI(role, content, messageId = null) {
    const messagesContainer = document.getElementById('tomerbot-messages');
    if (!messagesContainer) return;

    const messageDiv = document.createElement('div');
    const isUser = role === 'user';

    messageDiv.className = isUser ? 'user-message' : 'bot-message';

    // Handle HTML content in bot messages (for product links)
    if (isUser) {
      messageDiv.textContent = content;
    } else {
      // Convert markdown-style links to HTML
      let htmlContent = content.replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2" target="_blank" rel="noopener">$1</a>');

      // Clean up radio link formatting: remove "- " prefixes and <br> between radio links
      if (htmlContent.includes('class="radio"')) {
        htmlContent = htmlContent.replace(/-\s*(<a\s[^>]*class="radio")/g, '$1');
        htmlContent = htmlContent.replace(/<br\s*\/?>\s*(?=<a\s[^>]*class="radio")/g, '');
      }

      // Create message content wrapper
      const contentWrapper = document.createElement('div');
      contentWrapper.className = 'bot-message-content';
      contentWrapper.innerHTML = htmlContent;

      // Create feedback buttons container
      const feedbackContainer = document.createElement('div');
      feedbackContainer.className = 'bot-message-feedback';

      // Use provided message ID or generate a new one
      if (!messageId) {
        messageId = `msg_${Date.now()}_${Math.random().toString(36).substring(2, 11)}`;
      }
      feedbackContainer.setAttribute('data-message-id', messageId);

      feedbackContainer.innerHTML = `
      <span class="feedback-text">עזרת לי</span>
        <button class="feedback-btn like-btn" data-feedback="like" title="תשובה מועילה">
          <span class="feedback-icon"></span>
        </button>
        <button class="feedback-btn dislike-btn" data-feedback="dislike" title="תשובה לא מועילה">
          <span class="feedback-icon"></span>
        </button>
      `;

      // Handle radio-style links
      const radioLinks = contentWrapper.querySelectorAll('a.radio');

      // Restore saved radio selection
      const savedRadio = this.getRadioSelection(messageId);
      if (savedRadio) {
        radioLinks.forEach(r => {
          if (r.textContent.trim() === savedRadio) {
            r.classList.add('selected');
          }
        });
      }

      radioLinks.forEach(radio => {
        radio.addEventListener('click', (e) => {
          e.preventDefault();
          e.stopPropagation();

          // Deselect all radios in this message
          radioLinks.forEach(r => r.classList.remove('selected'));
          // Select the clicked one
          radio.classList.add('selected');

          // Save radio selection
          this.saveRadioSelection(messageId, radio.textContent.trim());

          // Send the selected text as a user message
          const selectedText = radio.textContent.trim();
          setTimeout(async () => {
            this.addMessage('user', selectedText);
            this.showTypingIndicator();
            try {
              const response = await this.client.sendMessageWithRetry(selectedText);
              this.hideTypingIndicator();
              this.addMessage('bot', response.message);
            } catch (error) {
              this.hideTypingIndicator();
              this.addMessage('bot', 'מצטערים, אירעה שגיאה. אנא נסו שוב.');
            }
          }, 300);
        });
      });

      messageDiv.appendChild(contentWrapper);
      messageDiv.appendChild(feedbackContainer);

      // Attach feedback event listeners
      const likeBtn = feedbackContainer.querySelector('.like-btn');
      const dislikeBtn = feedbackContainer.querySelector('.dislike-btn');

      likeBtn?.addEventListener('click', (e) => {
        e.stopPropagation();
        this.handleFeedback(messageId, 'like', content, likeBtn, dislikeBtn);
      });

      dislikeBtn?.addEventListener('click', (e) => {
        e.stopPropagation();
        this.handleFeedback(messageId, 'dislike', content, likeBtn, dislikeBtn);
      });

      // Restore saved feedback state
      const savedFeedback = this.messageFeedback[messageId];
      if (savedFeedback === 'like') {
        likeBtn?.classList.add('active');
        dislikeBtn?.classList.add('disabled');
      } else if (savedFeedback === 'dislike') {
        dislikeBtn?.classList.add('active');
        likeBtn?.classList.add('disabled');
      }
    }

    messagesContainer.appendChild(messageDiv);

    // Scroll to bottom
    messagesContainer.scrollTop = messagesContainer.scrollHeight;
  }
  
  showTypingIndicator() {
    const messagesContainer = document.getElementById('tomerbot-messages');
    if (!messagesContainer) return;
    
    const typingDiv = document.createElement('div');
    typingDiv.id = 'typing-indicator';
    typingDiv.className = 'bot-message typing-indicator';
    typingDiv.innerHTML = `
      <span class="typing-dots">
        <span></span>
        <span></span>
        <span></span>
      </span>
      <span class="typing-text">תומר מקליד...</span>
    `;
    
    messagesContainer.appendChild(typingDiv);
    messagesContainer.scrollTop = messagesContainer.scrollHeight;
  }
  
  hideTypingIndicator() {
    const typingIndicator = document.getElementById('typing-indicator');
    typingIndicator?.remove();
  }
  
  // Save conversation to sessionStorage for UI persistence
  saveConversationHistory() {
    try {
      sessionStorage.setItem('tomerbot_ui_messages', JSON.stringify(this.messages));
    } catch (error) {
      console.error('Error saving UI conversation:', error);
    }
  }

  // Load conversation from sessionStorage for UI display
  loadConversationHistory() {
    try {
      const saved = sessionStorage.getItem('tomerbot_ui_messages');
      if (saved) {
        this.messages = JSON.parse(saved);
      }
    } catch (error) {
      console.error('Error loading UI conversation:', error);
      this.messages = [];
    }
  }

  // Save radio selection per message
  saveRadioSelection(messageId, selectedText) {
    try {
      const saved = JSON.parse(sessionStorage.getItem('tomerbot_radio_selections') || '{}');
      saved[messageId] = selectedText;
      sessionStorage.setItem('tomerbot_radio_selections', JSON.stringify(saved));
    } catch (error) {
      console.error('Error saving radio selection:', error);
    }
  }

  // Get saved radio selection for a message
  getRadioSelection(messageId) {
    try {
      const saved = JSON.parse(sessionStorage.getItem('tomerbot_radio_selections') || '{}');
      return saved[messageId] || null;
    } catch (error) {
      return null;
    }
  }

  // Save feedback to sessionStorage
  saveFeedbackHistory() {
    try {
      sessionStorage.setItem('tomerbot_message_feedback', JSON.stringify(this.messageFeedback));
    } catch (error) {
      console.error('Error saving feedback:', error);
    }
  }

  // Load feedback from sessionStorage
  loadFeedbackHistory() {
    try {
      const saved = sessionStorage.getItem('tomerbot_message_feedback');
      if (saved) {
        this.messageFeedback = JSON.parse(saved);
      }
    } catch (error) {
      console.error('Error loading feedback:', error);
      this.messageFeedback = {};
    }
  }
  
  // Restore messages in UI
  restoreMessagesInUI() {
    const messagesContainer = document.getElementById('tomerbot-messages');
    if (!messagesContainer || this.messages.length === 0) return;

    // Clear default welcome message if we have saved messages
    messagesContainer.innerHTML = '';

    // Restore all messages with their stored IDs
    this.messages.forEach(msg => {
      this.addMessageToUI(msg.role, msg.content, msg.id);
    });

    // Jump to the last message instantly (no smooth scroll)
    setTimeout(() => {
      messagesContainer.style.scrollBehavior = 'auto';
      messagesContainer.scrollTop = messagesContainer.scrollHeight;
      messagesContainer.style.scrollBehavior = '';
    }, 100);
    
    // If no messages, show welcome message
    if (this.messages.length === 0) {
              messagesContainer.innerHTML = `
              
          <div class="bot-message welcome-message">
            <div class="welcome-header">
              
              <div class="welcome-text">
                <h4>שלום! אני תומר</h4>
                <p>העוזר האישי שלכם בהום סנטר</p>
              </div>
            </div>
            <div class="welcome-content">
              <p>אני מתמחה בייעוץ מקצועי לבית, המלצות מוצרים ומידע על מלאי.</p>
              <div class="welcome-cta">
                <strong>במה אוכל לעזור לך?</strong>
              </div>
            </div>
          </div>
        `;
    }
  }
  
  // Clear conversation
  clearConversation() {
    if (confirm(this.isRTL ? 'האם אתם בטוחים שברצונכם להתחיל שיחה חדשה?' : 'Are you sure you want to start a new conversation?')) {
      this.messages = [];
      this.messageFeedback = {}; // Clear feedback storage
      this.client.clearHistory();
      sessionStorage.removeItem('tomerbot_ui_messages');
      sessionStorage.removeItem('tomerbot_message_feedback');
      sessionStorage.removeItem('tomerbot_radio_selections');

      // Check if user has accepted privacy policy
      const hasAccepted = sessionStorage.getItem('tomerbot_privacy_accepted') === 'true';

      // Clear UI and show welcome message
      const messagesContainer = document.getElementById('tomerbot-messages');
      const heading = 'אני תומר, היועץ הדיגיטלי של הום סנטר.';
      const body = `אשמח להציע לך פתרונות ומוצרים לתחזוקת הבית.
יתכן שתשובותיי לא תמיד תהיינה מדויקות, וידרשו פנייה לאיש מקצוע.`;
      const agreement = `בטרם נתחיל, עליי לקבל את הסכמתך כי שיחתנו תתקיים בהתאם <a href="/pages/ai-tomer-terms-of-use" target="_blank">לתנאי השימוש</a> <a href="/pages/tomer-ai-privacy-policy" target="_blank">ומדיניות הפרטיות</a>`;

      // Build mini form HTML only if user hasn't accepted
      const miniFormHTML = hasAccepted ? '' : `
        <div class="mini-agreement-form">
          <p class="agreement-text">${agreement}</p>
          <button id="accecpts_privacy_policy"><span class="agree-icon"></span></button>
        </div>
      `;

      if (messagesContainer) {
        messagesContainer.innerHTML = `
          <div class="bot-message welcome-message">
            <div class="welcome-header">
              <div class="welcome-text">
                <h4>${heading}</h4>
              </div>
            </div>
            <div class="welcome-content">
              <p>${body}</p>
              ${miniFormHTML}
            </div>
          </div>
        `;

        // Re-attach event listener to the new button if mini form was added
        if (!hasAccepted) {
          const privacyButton = document.getElementById('accecpts_privacy_policy');
          privacyButton?.addEventListener('click', () => {
            this.handlePrivacyPolicyButton();
          });
        }
      }
    }
  }
  
  // Privacy policy button handler
  handlePrivacyPolicyButton() {
    // Store acceptance in session storage
    sessionStorage.setItem('tomerbot_privacy_accepted', 'true');

    // Enable chat by removing disabled class from tomerbot-chat
    const chatWindow = document.getElementById('tomerbot-chat');
    if (chatWindow) {
      chatWindow.classList.remove('disabled');
    }

    // Also remove disabled attribute from input field
    const inputField = document.getElementById('tomerbot-input-field');
    if (inputField) {
      inputField.disabled = false;
      inputField.removeAttribute('disabled');
    }

    // Hide the mini agreement form
    const miniForm = document.querySelector('.mini-agreement-form');
    if (miniForm) {
      miniForm.style.display = 'none';
    }

    // Focus on input field
    setTimeout(() => {
      inputField?.focus();
    }, 100);
  }

  // Check privacy policy acceptance on initialization
  checkPrivacyPolicyAcceptance() {
    const hasAccepted = sessionStorage.getItem('tomerbot_privacy_accepted') === 'true';

    if (hasAccepted) {
      // Use setTimeout to ensure DOM is fully rendered
      setTimeout(() => {
        // Enable chat by removing disabled class from tomerbot-chat
        const chatWindow = document.getElementById('tomerbot-chat');
        if (chatWindow) {
          chatWindow.classList.remove('disabled');
        }

        // Also remove disabled attribute from input field
        const inputField = document.getElementById('tomerbot-input-field');
        if (inputField) {
          inputField.disabled = false;
          inputField.removeAttribute('disabled');
        }

        // Hide the mini agreement form since user already accepted
        const miniForm = document.querySelector('.mini-agreement-form');
        if (miniForm) {
          miniForm.style.display = 'none';
        }

        // Hide welcome message if we have messages
        if (this.messages.length > 0) {
          const welcomeMessage = document.querySelector('.welcome-message');
          if (welcomeMessage) {
            welcomeMessage.style.display = 'none';
          }
        }
      }, 0);
    }
  }

  // Handle feedback (like/dislike) for bot messages
  async handleFeedback(messageId, feedbackType, messageContent, likeBtn, dislikeBtn) {
    console.log('TomerBot Feedback:', {
      messageId,
      feedbackType,
      messageContent,
      sessionId: this.client.getSessionId(),
      timestamp: new Date().toISOString()
    });

    // Toggle button states
    if (feedbackType === 'like') {
      if (likeBtn.classList.contains('active')) {
        // Deactivate if already active
        likeBtn.classList.remove('active');
        dislikeBtn.classList.remove('disabled');
        // Remove from storage
        delete this.messageFeedback[messageId];
        this.saveFeedbackHistory();
        return; // Don't send feedback if deactivating
      } else {
        // Activate like, deactivate dislike
        likeBtn.classList.add('active');
        dislikeBtn.classList.remove('active');
        dislikeBtn.classList.add('disabled');
        // Save to storage
        this.messageFeedback[messageId] = 'like';
        this.saveFeedbackHistory();
      }
    } else if (feedbackType === 'dislike') {
      if (dislikeBtn.classList.contains('active')) {
        // Deactivate if already active
        dislikeBtn.classList.remove('active');
        likeBtn.classList.remove('disabled');
        // Remove from storage
        delete this.messageFeedback[messageId];
        this.saveFeedbackHistory();
        return; // Don't send feedback if deactivating
      } else {
        // Activate dislike, deactivate like
        dislikeBtn.classList.add('active');
        likeBtn.classList.remove('active');
        likeBtn.classList.add('disabled');
        // Save to storage
        this.messageFeedback[messageId] = 'dislike';
        this.saveFeedbackHistory();
      }
    }

    // Send feedback to service
    await this.sendFeedbackToService(feedbackType, messageContent);
  }

  // Send feedback to service using the same API as messages
  async sendFeedbackToService(feedbackType, messageContent) {
    try {
      // Build request payload with feedback data
      const payload = {
        user_query: '', // Empty as per requirement
        session_id: this.client.getSessionId(),
        messages: [], // Empty array as per requirement
        feedback: feedbackType === 'like' ? true : false, // Boolean: true for like, false for dislike
        feedback_for_message: messageContent // The actual message content
      };

      // Add customer data if user is logged in
      const customerData = ShopifyCustomerDetector.getCustomerData();
      if (customerData) {
        payload.customer = customerData;
      }

      console.log('TomerBot - Sending feedback:', payload);

      // Send feedback using the same API endpoint as messages
      const response = await fetch(this.client.apiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload)
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const data = await response.json();
      console.log('TomerBot - Feedback sent successfully:', data);

      return {
        success: true,
        data: data
      };

    } catch (error) {
      console.error('TomerBot - Error sending feedback:', error);
      return {
        success: false,
        error: error.message
      };
    }
  }

  // Public method to get conversation data
  getConversationData() {
    return {
      messages: this.messages,
      sessionId: this.client.getSessionId(),
      isOpen: this.isOpen
    };
  }
}

// Global initialization function
window.initTomerBot = function(containerId, options = {}) {
  return new TomerBotWidget(containerId, options);
};

// Auto-initialize if container exists
document.addEventListener('DOMContentLoaded', function() {
  const container = document.getElementById('tomerbot-container');
  if (container && !window.tomerBotInstance) {
    window.tomerBotInstance = new TomerBotWidget('tomerbot-container', {
      apiBaseUrl: '', // Use default API URL
      rtl: true // Always RTL for Hebrew
    });
  }
});

// Export for module systems
if (typeof module !== 'undefined' && module.exports) {
  module.exports = { TomerBotWidget, TomerBotClient, TomerBotSession, ShopifyCustomerDetector };
}